import { useEffect, useRef } from 'react';

import { BREADCRUMBS_MODES } from '@confluence/breadcrumbs';
import {
	confluenceLocalStorageInstance,
	PERSISTED_KEYS_ON_SERVER,
} from '@confluence/storage-manager';
import { fg } from '@confluence/feature-gating';

const getIsSideNavCollapsed = () => {
	const sideNavCollapsedValue =
		confluenceLocalStorageInstance.getItem(
			PERSISTED_KEYS_ON_SERVER.PERSISTED_LEFT_SIDEBAR_COLLAPSED,
		) ?? 'expanded';
	return sideNavCollapsedValue === 'collapsed';
};

type UseBreadcrumbsDisplayModeType = {
	contentType: string;
	isSpaceOverviewPage: boolean;
	isVersionHistoryPage: boolean;
	isArchived: boolean;
	isEditorToolbarShown: boolean;
	isTitleFullyVisible?: boolean;
	isObjectSidebarShown: boolean;
	contentId?: string;
	contentTypesHeaderDataLoading?: boolean;
};

export const useBreadcrumbsDisplayMode = ({
	contentType,
	isSpaceOverviewPage,
	isVersionHistoryPage,
	isArchived,
	isEditorToolbarShown,
	isTitleFullyVisible,
	isObjectSidebarShown,
	contentId,
	contentTypesHeaderDataLoading,
}: UseBreadcrumbsDisplayModeType): BREADCRUMBS_MODES => {
	const hasMounted = useRef(false);

	// only live pages, page, blogs, and editor (when toolbar is not shown) care about title visibility (when user scrolls past page title)
	// except for space overview and archived pages and version history
	const shouldListenForTitleVisibilityChanges =
		(contentType === 'blogpost' || contentType === 'page') &&
		!isSpaceOverviewPage &&
		!isArchived &&
		!isEditorToolbarShown &&
		!isVersionHistoryPage;

	// this useEffect duplicates logic for FULL, ONLY_CONTENT_TITLE, ONLY_SPACE_NAME, and HIDDEN breadcrumbs
	// this is required because using react state to conditionally render parts of breadcrumbs is simply not fast enough and causes jumpiness in the breadcrumbs on side nav collapse/expand
	// AND we need to fine-tuned control over fade in of content title breadcrumbs
	useEffect(() => {
		const toggleSpaceNameBreadcrumbListener = () => {
			const separator = document.getElementById('breadcrumbs-separator');
			const spaceName = document.getElementById('breadcrumbs-space-name');
			const chevron = document.getElementById('breadcrumbs-dropdown-chevron-button');
			const contentTitle = document.getElementById('breadcrumbs-content-title');

			const showOnlyContentTitle = () => {
				separator?.setAttribute('style', 'display: none;');
				spaceName?.setAttribute('style', 'display: none;');
				chevron?.setAttribute('style', 'display: block;');
				if (contentTitle) {
					contentTitle.style.opacity = '1';
					contentTitle.style.maxWidth = '800px';
				}
			};

			const showOnlySpaceName = () => {
				separator?.setAttribute('style', 'display: none;');
				spaceName?.setAttribute('style', 'display: inline;');
				chevron?.setAttribute('style', 'display: block;');
				if (contentTitle) {
					contentTitle.style.opacity = '0';
					contentTitle.style.maxWidth = '0px';
				}
			};

			const showFullBreadcrumbs = () => {
				separator?.setAttribute('style', 'display: inline;');
				spaceName?.setAttribute('style', 'display: inline;');
				chevron?.setAttribute('style', 'display: block;');
				if (contentTitle) {
					contentTitle.style.opacity = '1';
					contentTitle.style.maxWidth = '800px';
				}
			};

			const hideEntireBreadcrumbs = () => {
				separator?.setAttribute('style', 'display: none;');
				spaceName?.setAttribute('style', 'display: none;');
				chevron?.setAttribute('style', 'display: none;');
				if (contentTitle) {
					contentTitle.style.opacity = '0';
					contentTitle.style.maxWidth = '0px';
				}
			};

			if (!isEditorToolbarShown) {
				if (contentTitle) {
					contentTitle.style.transition = 'opacity 100ms linear';
				}
			}

			// these are the only two places where we do not want to hide space name on collapse of side nav
			if (!isSpaceOverviewPage && !isVersionHistoryPage) {
				if (getIsSideNavCollapsed()) {
					if (shouldListenForTitleVisibilityChanges) {
						if (isTitleFullyVisible) {
							// SIDE NAV COLLAPSED + TITLE IS SHOWING = ONLY SPACE NAME
							showOnlySpaceName();
						} else {
							if (contentTypesHeaderDataLoading) {
								hideEntireBreadcrumbs();
							} else {
								// SIDE NAV COLLAPSED + TITLE IS NOT SHOWING = FULL
								showFullBreadcrumbs();
							}
						}
					} else {
						if (isEditorToolbarShown) {
							// for when user swaps between contextual toolbar and docked toolbar
							showFullBreadcrumbs();
						}
						// for all other cases that don't care about title visibility, simply show space name and separator
						separator?.setAttribute('style', 'display: inline;');
						spaceName?.setAttribute('style', 'display: inline;');
					}
				} else {
					if (shouldListenForTitleVisibilityChanges) {
						if (isTitleFullyVisible) {
							if (isObjectSidebarShown) {
								// in the case where you are at the top of the page and the object sidebar is open, we should force show breadcrumbs and border so the sidebar isn't floating there by itself
								showOnlyContentTitle();
							} else {
								// SIDE NAV EXPANDED + TITLE IS SHOWING = NO BREADCRUMBS
								hideEntireBreadcrumbs();
							}
						} else {
							if (contentTypesHeaderDataLoading) {
								hideEntireBreadcrumbs();
							} else {
								// SIDE NAV EXPANDED + TITLE IS COVERED = CONTENT TITLE ONLY
								showOnlyContentTitle();
							}
						}
					} else {
						if (isEditorToolbarShown) {
							// for when user swaps between contextual toolbar and docked toolbar
							showOnlyContentTitle();
						}
						// for all other cases that don't care about title visibility, simply hide space name and separator
						separator?.setAttribute('style', 'display: none;');
						spaceName?.setAttribute('style', 'display: none;');
					}
				}
			}
		};

		if (fg('confluence_frontend_breadcrumbs_dom_manipulation')) {
			window.addEventListener('resize', toggleSpaceNameBreadcrumbListener); // listen for side nav collapse and expand and other resize events
			if (!hasMounted.current) {
				hasMounted.current = true;
			} else {
				// don't run this on initial render of SSRed page since it will become a ttvc offender
				toggleSpaceNameBreadcrumbListener();
			}
		}

		return () => {
			window.removeEventListener('resize', toggleSpaceNameBreadcrumbListener);
		};
	}, [
		isObjectSidebarShown,
		shouldListenForTitleVisibilityChanges,
		isSpaceOverviewPage,
		isVersionHistoryPage,
		contentId,
		isTitleFullyVisible,
		isEditorToolbarShown,
		contentTypesHeaderDataLoading,
	]);

	// NOTE: If you are changing any of the logic below for display modes, you will likely need to update the useEffect above to match accordingly.
	if (!isVersionHistoryPage) {
		// all live pages, page, blogs, and editor except for space overview and archived pages
		if (
			(contentType === 'blogpost' || contentType === 'page') &&
			!isSpaceOverviewPage &&
			!isArchived
		) {
			if (
				contentTypesHeaderDataLoading &&
				!isTitleFullyVisible &&
				fg('confluence_frontend_breadcrumbs_dom_manipulation')
			) {
				return BREADCRUMBS_MODES.HIDDEN;
			}

			if (isEditorToolbarShown) {
				// don't care about title visibility
				if (getIsSideNavCollapsed()) {
					return BREADCRUMBS_MODES.FULL;
				} else {
					return BREADCRUMBS_MODES.ONLY_CONTENT_TITLE;
				}
			} else {
				// care about title visibility (aka view page or blogpost, live page view mode)
				if (getIsSideNavCollapsed()) {
					// SIDE NAV COLLAPSED + TITLE IS SHOWING = ONLY SPACE NAME
					if (isTitleFullyVisible) {
						return BREADCRUMBS_MODES.ONLY_SPACE_NAME;
					} else {
						// SIDE NAV COLLAPSED + TITLE IS NOT SHOWING = FULL
						return BREADCRUMBS_MODES.FULL;
					}
				} else {
					// SIDE NAV EXPANDED + TITLE IS SHOWING = NO BREADCRUMBS
					if (isTitleFullyVisible) {
						if (isObjectSidebarShown) {
							// in the case where you are at the top of the page and the object sidebar is open, we should force show breadcrumbs and border so the sidebar isn't floating there by itself
							return BREADCRUMBS_MODES.ONLY_CONTENT_TITLE;
						}
						return BREADCRUMBS_MODES.HIDDEN;
					} else {
						// SIDE NAV EXPANDED + TITLE IS COVERED = CONTENT TITLE ONLY
						return BREADCRUMBS_MODES.ONLY_CONTENT_TITLE;
					}
				}
			}
			// all other content types
		} else if (
			contentType === 'database' ||
			contentType === 'whiteboard' ||
			contentType === 'embed'
		) {
			if (getIsSideNavCollapsed()) {
				return BREADCRUMBS_MODES.FULL;
			} else {
				return BREADCRUMBS_MODES.ONLY_CONTENT_TITLE;
			}
		}
	}

	return BREADCRUMBS_MODES.FULL;
};
